import env from '@medipass/utils/get-env';

import type { PayPalCheckoutConfig, PayPalSDK } from './types';

const scripts = new Map<string, Promise<PayPalSDK>>();
let namespaceId = 0;

export function loadPayPalSDK({
  clientId,
  accountId,
  currencyCode,
}: Pick<PayPalCheckoutConfig, 'clientId' | 'accountId' | 'currencyCode'>): Promise<PayPalSDK> {
  const params = new URLSearchParams({
    'client-id': clientId,
    'merchant-id': accountId,
    intent: 'capture',
    currency: currencyCode,
    components: 'messages,buttons',
    'enable-funding': 'paylater',
    locale: 'en_AU',
  });
  // Match the test payment environments in App.tsx; never force buyer country for live payments.
  if (['local', 'dev', 'staging', 'perf'].includes(env.ENV_NAME ?? '')) {
    params.set('buyer-country', 'AU');
  }
  const url = `https://www.paypal.com/sdk/js?${params}`;
  const existing = scripts.get(url);
  if (existing) return existing;

  // Separate namespaces prevent a different merchant/currency from replacing a live SDK instance.
  const namespace = `medipassPayPal${++namespaceId}`;
  const promise = new Promise<PayPalSDK>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.dataset.namespace = namespace;
    const globals = window as unknown as Record<string, PayPalSDK | undefined>;
    const fail = () => {
      window.clearTimeout(timeout);
      script.remove();
      delete globals[namespace];
      reject(new Error('PayPal could not be loaded. Please try again or choose another payment method.'));
    };
    const timeout = window.setTimeout(fail, 30000);
    script.onerror = fail;
    script.onload = () => {
      const paypal = globals[namespace];
      if (!paypal?.Buttons) return fail();
      window.clearTimeout(timeout);
      resolve(paypal);
    };
    document.head.appendChild(script);
  }).catch(error => {
    scripts.delete(url);
    throw error;
  });
  scripts.set(url, promise);
  return promise;
}
